import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";

import { cx } from "../lib/cx";

/**
 * 버튼.
 *
 * **크기와 색을 분리합니다.** 높이·글자·여백은 `size` 만, 색·테두리·상태색은 `variant` 만 정합니다.
 * 예전에는 크기가 변형에 묶여 있어 같은 `md` 라도 primary 44px · ghost 36px 로 달랐고, 모달
 * 푸터의 "취소/저장" 이 늘 8px 어긋났습니다.
 *
 *  - `sm` 32px — 표 행 안의 동작. 모바일(< 640px)에서는 36px 로 키워 터치 영역을 확보합니다.
 *  - `md` 40px — 입력 컨트롤(`Field` 의 40px)과 같은 높이. 모달 푸터·페이지 동작·헤더.
 *  - `lg` 52px — 로그인 버튼 전용.
 *
 * 비활성 스타일도 **변형별로** 정의합니다. 모든 변형에 회색 채움을 쓰면 테두리 버튼 줄에서 누를 수
 * 없는 버튼이 가장 무겁게 보입니다(강조 역전). primary 만 채움을 회색으로 바꾸고, 테두리형은 형태를
 * 유지한 채 색만 뺍니다.
 *
 * Tailwind 는 같은 속성의 유틸리티가 겹치면 "클래스 문자열의 순서" 가 아니라 "생성된 CSS 의 순서" 로
 * 승자가 정해집니다. 그래서 크기 문자열과 변형 문자열은 **서로 다른 속성만** 다루고, 호출부에서
 * `className` 으로 높이·색을 덮어쓰지 않습니다(필요하면 `size` 를 고릅니다). `disabled:` 는 variant
 * 순서상 `hover:` 보다 뒤에 출력되므로 비활성 상태가 hover 색을 이깁니다.
 *
 * 화면 규칙(일관성을 위해 지킵니다)
 *  - 권한이 없는 동작은 **숨깁니다.** 권한은 있지만 지금 조건이 맞지 않으면 **비활성 + 이유를 보이게** 둡니다.
 *  - 선택 하나로 결정되는 동작(역할 고르기 등)은 조건이 찰 때까지 비활성. 여러 입력의 폼은 항상 활성으로
 *    두고 누르면 어느 칸이 문제인지 알려 줍니다.
 *  - 서버를 부르는 동작은 응답까지 `pending` 으로 둡니다 — 중복 제출을 막습니다.
 */

export type ButtonVariant = "primary" | "ghost" | "danger" | "google";
export type ButtonSize = "sm" | "md" | "lg";

const BASE =
  "relative inline-flex items-center justify-center gap-[8px] whitespace-nowrap rounded-xl border " +
  "font-sans font-semibold disabled:cursor-not-allowed";

/** 색 전환 — google 은 그림자까지 전환하므로 따로 둡니다(transition-property 를 겹쳐 쓰지 않기 위해). */
const COLOR_TRANSITION = "transition-colors duration-[120ms] ";

const SIZE: Record<ButtonSize, string> = {
  sm: "h-[36px] px-[12px] text-[13px] sm:h-[32px] sm:px-[10px] sm:text-[12px]",
  md: "h-[40px] px-[16px] text-[14px]",
  lg: "h-[52px] px-[20px] text-[15px]",
};

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    COLOR_TRANSITION +
    "border-transparent bg-blue-500 text-white hover:bg-blue-700 active:bg-blue-800 " +
    "disabled:bg-grey-200 disabled:text-grey-500",
  ghost:
    COLOR_TRANSITION +
    "border-grey-200 bg-white text-grey-700 hover:border-grey-400 hover:bg-grey-050 " +
    "disabled:border-grey-100 disabled:bg-white disabled:text-grey-300",
  // red-500 — red-300 은 흰 배경 대비 3.2:1 로 12~13px 글자 기준(4.5:1)에 못 미칩니다(red-500 은 5.0:1).
  danger:
    COLOR_TRANSITION +
    "border-grey-200 bg-white text-red-500 hover:border-red-500 hover:bg-red-100 " +
    "disabled:border-grey-100 disabled:bg-white disabled:text-grey-300",
  // 연구노트 관리 로그인 버튼과 같은 결 — 흰 바탕 유지, hover 는 테두리를 한 단계 진하게 + 그림자.
  // `group` 은 안쪽 화살표의 hover 이동용입니다.
  google:
    "group w-full gap-[12px] border-grey-100 bg-white text-grey-800 " +
    "transition-[border-color,box-shadow] duration-200 hover:border-grey-200 " +
    "hover:shadow-[0_6px_16px_rgba(25,25,28,0.08)] active:bg-grey-050",
};

/** 모듈 내부 전용 — 밖으로 내보내면 Fast Refresh 가 이 파일의 컴포넌트 상태를 보존하지 못합니다. */
const buttonClass = (variant: ButtonVariant = "primary", size: ButtonSize = "md"): string =>
  cx(BASE, SIZE[size], VARIANT[variant]);

/** 진행 표시 — 동작 확인용 회전이라 움직임 줄이기 설정이면 멈춥니다. */
function Spinner() {
  return (
    <svg
      aria-hidden="true"
      className="h-[14px] w-[14px] shrink-0 animate-spin motion-reduce:animate-none"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 서버 응답 대기 중 — 비활성 + 진행 표시. 라벨은 그대로 둬 버튼 폭과 의미가 바뀌지 않습니다. */
  pending?: boolean;
  children: ReactNode;
}

export function Button({
  variant,
  size,
  pending = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    // type 을 명시하는 이유: 기본값 "submit" 은 폼 안에서 의도치 않은 제출을 일으킵니다
    // (`server.js` 의 CSP 가 `form-action 'none'` 이라 제출은 조용히 실패합니다 — 더 찾기 어려운 버그입니다).
    <button
      type="button"
      className={cx(buttonClass(variant, size), className)}
      disabled={pending || disabled}
      aria-busy={pending || undefined}
      {...rest}
    >
      {pending ? <Spinner /> : null}
      {children}
    </button>
  );
}

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

/** 버튼 모양의 링크 — Google 로그인처럼 **다른 origin 으로 나가는** 이동에 씁니다. */
export function ButtonLink({ variant, size, className, children, ...rest }: ButtonLinkProps) {
  return (
    <a className={cx(buttonClass(variant, size), "hover:no-underline", className)} {...rest}>
      {children}
    </a>
  );
}
